<script setup>
import { computed, ref } from 'vue';
import ContactPanel from 'dashboard/routes/dashboard/conversation/ContactPanel.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import wootConstants from 'dashboard/constants/globals';
import { useMapGetter } from 'dashboard/composables/store';

const props = defineProps({
  currentChat: {
    required: true,
    type: Object,
  },
});

const { uiSettings, updateUISettings } = useUISettings();
const { width: windowWidth } = useWindowSize();

const activeTab = computed(() => {
  const { is_contact_sidebar_open: isContactSidebarOpen } = uiSettings.value;
  return isContactSidebarOpen ? 0 : null;
});

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);

const closeContactPanel = () => {
  if (isSmallScreen.value && uiSettings.value?.is_contact_sidebar_open) {
    updateUISettings({
      is_contact_sidebar_open: false,
      is_copilot_panel_open: false,
    });
  }
};

/* ======== WHMCS custom section (kept as you had) ======== */
const overrideEmail = ref('');
const selectedDept = ref('2');
const contactEmail = computed(
  () => props.currentChat?.meta?.sender?.email || ''
);

function extractEmailFromMessages(messages) {
  const emailRegex =
    /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;
  for (const message of messages) {
    const match = message?.content?.match(emailRegex);
    if (match) return match[0];
  }
  return null;
}

const currentAccountId = useMapGetter('getCurrentAccountId');

const handleCreateTicket = async () => {
  try {
    const accountId = currentAccountId.value;
    const conversationId = props.currentChat.id;

    // подтягиваем сообщения, чтобы, если нужно, выдернуть email из текста
    const messagesResponse = await fetch(
      `/api/v1/accounts/${accountId}/conversations/${conversationId}/messages`
    );
    const messagesData = await messagesResponse.json();
    const messages = messagesData?.payload || [];

    const fallbackEmail = extractEmailFromMessages(messages);
    const finalEmail =
      overrideEmail.value?.trim() ||
      contactEmail.value?.trim() ||
      fallbackEmail;

    if (!finalEmail) {
      window.alert('Email not found. Please enter it manually.');
      return;
    }

    const response = await fetch(`/chatwoot-to-whmcs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token':
          document.querySelector('meta[name="csrf-token"]')?.content || '',
      },
      body: JSON.stringify({
        account_id: accountId,
        conversation_id: conversationId,
        email: finalEmail,
        deptid: selectedDept.value,
      }),
      credentials: 'same-origin',
    });

    const result = await response.json();

    if (result?.result === 'success') {
      window.alert('Ticket created in WHMCS!');
    } else {
      window.alert(result?.error || 'Ticket creation failed.');
    }
  } catch (error) {
    console.error('Error while creating ticket:', error);
    window.alert('Error: ' + error.message);
  }
};
/* ======== /WHMCS custom section ======== */
</script>

<template>
  <div
    v-on-click-outside="() => closeContactPanel()"
    class="bg-n-background h-full overflow-hidden flex flex-col fixed top-0 z-40 w-full max-w-sm transition-transform duration-300 ease-in-out ltr:right-0 rtl:left-0 md:static md:w-[320px] md:min-w-[320px] ltr:border-l rtl:border-r border-n-weak 2xl:min-w-[360px] 2xl:w-[360px] shadow-lg md:shadow-none"
    :class="[
      {
        'md:flex': activeTab === 0,
        'md:hidden': activeTab !== 0,
      },
    ]"
  >
    <div class="flex flex-1 overflow-auto flex-col">
      <!-- WHMCS панель управления (как у тебя было) -->
      <div v-if="activeTab === 0" class="p-2 border-b border-n-100">
        <div class="flex flex-col gap-2">
          <button
            class="bg-woot-500 hover:bg-woot-600 text-white text-sm font-medium px-3 py-1 rounded w-full"
            @click="handleCreateTicket"
          >
            Create Ticket in WHMCS
          </button>
          <div>
            <label class="text-xs text-gray-500">
              Email for ticket (optional)
            </label>
            <input
              v-model="overrideEmail"
              placeholder="example@example.com"
              class="w-full border rounded px-2 py-1 text-sm mt-1"
              type="email"
            />
          </div>
          <div>
            <label class="text-xs text-gray-500">Department</label>
            <select
              v-model="selectedDept"
              class="w-full border rounded px-2 py-1 text-sm mt-1"
            >
              <option value="1">Administrative</option>
              <option value="2">Technical</option>
              <option value="3">Financial</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Боковая панель контакта (оригинал 4.6.0) -->
      <ContactPanel
        v-show="activeTab === 0"
        :conversation-id="currentChat.id"
        :inbox-id="currentChat.inbox_id"
      />
    </div>
  </div>
</template>

